'use client';

import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Drawer,
  Typography,
  Box,
  Button,
  CircularProgress,
} from 'src/components';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import {
  useGetProviderCohortByIdQuery,
  useUpdateProviderCohortMutation,
} from 'src/api/redux/providerCohorts';
import useGetAccountFolders from 'src/hooks/accountFolders/useGetAccountFolders';
import { useEncounters } from 'src/context/providers/EncountersProvider';
import { useNotification } from 'src/context/notification';
import { BillingCodes, SaveVolumeType } from '../CreateVolumeType/components';
import SearchPreview from '../EncountersDrawer/components/SearchPreview';
import { SaveNewVolumeDialog } from '../SaveNewVolumeDialog';
import { SavedType, VolumeType, initialEncountersState, EncountersActionTypes } from 'src/context/reducers/encountersReducer';
import {
  buildCohortLanguage,
  filtersFromCohortLanguage,
} from 'src/components/Encounters/utils';

const EditEncountersDrawer = (): React.ReactElement => {
  const { isDrawerOpen, closeDrawer } = useDrawer();
  const { state, dispatch } = useEncounters();
  const { setNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [originalState, setOriginalState] = useState<any>(null);
  const [isSaveAsNewDialogOpen, setIsSaveAsNewDialogOpen] = useState(false);

  // Ref to track the volumeId we've initialized with
  const initializedVolumeIdRef = useRef<string | null>(null);

  // Get account folders mutation function
  const { mutate: mutateAccountFolders } = useGetAccountFolders();

  // RTK Query mutation for updating cohorts
  const [updateCohort] = useUpdateProviderCohortMutation();

  // Get saved volume functions to update selected volume if needed
  const location = state.editingLocation || 'discovery';
  const { appliedSavedVolume, setSavedVolume } = useSavedVolume(location);

  // Fetch volume data using ID from context
  const shouldFetch =
    state.editingVolumeId && state.editingVolumeType === 'cohort';
  const {
    data,
    error,
    isLoading: isFetching,
  } = useGetProviderCohortByIdQuery(state.editingVolumeId || '', {
    skip: !shouldFetch,
  });

  // Create the reset state using useMemo to avoid recalculations
  const resetState = useMemo(() => {
    // Only process data if this is the correct type
    if (!data || error || state.editingVolumeType !== 'cohort') return null;

    // Create the reset state object
    const newResetState = {
      ...initialEncountersState,
      editingVolumeId: state.editingVolumeId,
      newCohortName: data.label,
      selectedType:
        data.scope?.toLowerCase() === 'personal'
          ? SavedType.PERSONAL
          : SavedType.COMPANY,
      volumeType: VolumeType.ENCOUNTERS, // Explicitly set to ENCOUNTERS
      billingCodes: {
        anyAllOperator: data.cohort_language.operator,
        groups: [],
      },
    };

    // Process code groups
    if (
      data.cohort_language.children &&
      data.cohort_language.children.length > 0
    ) {
      const newGroups = data.cohort_language.children.map((childGroup) => {
        // Map API code format to app format
        const mappedCodes = childGroup.codes.map((apiCode) => ({
          id: `${apiCode.code_type}-${apiCode.code_name}`,
          code: apiCode.code_name,
          context_type: apiCode.code_type,
          description: '',
          modifier: apiCode.modifier,
        }));

        // Generate a new group ID
        const groupId = `group-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 9)}`;

        return {
          id: groupId,
          operator: childGroup.operator === 'AND' ? 'ALL' : 'ANY',
          inverse: childGroup.inverse,
          codes: mappedCodes,
        };
      });

      newResetState.billingCodes.groups = newGroups;
    }

    return newResetState;
  }, [data, error, state.editingVolumeId, state.editingVolumeType]);

  // Handle initialization when data is loaded and resetState is ready
  useEffect(() => {
    if (
      resetState &&
      state.editingVolumeId &&
      state.editingVolumeType === 'cohort' &&
      initializedVolumeIdRef.current !== state.editingVolumeId
    ) {
      // Store the original state for comparison
      setOriginalState({
        newCohortName: resetState.newCohortName,
        selectedType: resetState.selectedType,
        billingCodes: JSON.parse(JSON.stringify(resetState.billingCodes)),
      });

      // Always reset on first initialization - user hasn't had a chance to make changes yet
      dispatch({
        type: EncountersActionTypes.RESET_ENCOUNTERS_STATE,
        payload: resetState,
      });

      // Mark this volume as initialized
      initializedVolumeIdRef.current = state.editingVolumeId;
    }
  }, [resetState, state.editingVolumeId, state.editingVolumeType, dispatch]);

  // Reset the initialization ref when drawer closes
  useEffect(() => {
    if (!isDrawerOpen('editEncountersDrawer')) {
      initializedVolumeIdRef.current = null;
    }
  }, [isDrawerOpen]);

  const handleClose = () => {
    if (isSaveAsNewDialogOpen) return;
    // Reset SaveAsNew dialog state
    setIsSaveAsNewDialogOpen(false);

    // Clear editing state
    dispatch({
      type: EncountersActionTypes.SET_EDITING_GROUP_ID,
      payload: { groupId: null },
    });
    dispatch({
      type: EncountersActionTypes.SET_EDITING_VOLUME_ID,
      payload: { volumeId: null },
    });
    dispatch({
      type: EncountersActionTypes.SET_EDITING_VOLUME_TYPE,
      payload: { volumeType: null },
    });
    closeDrawer('editEncountersDrawer');
  };

  const handleSaveAsNew = () => {
    setIsSaveAsNewDialogOpen(true);
  };

  // Helper functions to detect specific field changes
  const hasNameChanged = () => {
    return originalState && state.newCohortName !== originalState.newCohortName;
  };

  const hasTypeChanged = () => {
    return originalState && state.selectedType !== originalState.selectedType;
  };

  const hasBillingCodesChanged = () => {
    if (!originalState) return false;

    // Compare top-level operator
    if (
      state.billingCodes.anyAllOperator !==
      originalState.billingCodes.anyAllOperator
    )
      return true;

    // Compare groups - we'll do a more thorough check
    if (
      state.billingCodes.groups.length !==
      originalState.billingCodes.groups.length
    )
      return true;

    // Check each group's properties and codes
    for (let i = 0; i < state.billingCodes.groups.length; i++) {
      const currentGroup = state.billingCodes.groups[i];
      const originalGroup = originalState.billingCodes.groups[i];

      // Check operator and inverse
      if (currentGroup.operator !== originalGroup.operator) return true;
      if (currentGroup.inverse !== originalGroup.inverse) return true;

      // Check codes length
      if (currentGroup.codes.length !== originalGroup.codes.length) return true;

      // Check each code
      for (let j = 0; j < currentGroup.codes.length; j++) {
        if (currentGroup.codes[j].id !== originalGroup.codes[j].id) return true;
        if (currentGroup.codes[j].modifier !== originalGroup.codes[j].modifier)
          return true;
      }
    }

    return false;
  };

  // Updated function to build partial update request body
  const buildUpdateRequestBody = () => {
    if (!originalState) return { label: state.newCohortName };

    const updateBody: any = {};

    if (hasNameChanged()) {
      updateBody.label = state.newCohortName;
    }

    if (hasTypeChanged()) {
      updateBody.scope = state.selectedType;
    }

    if (hasBillingCodesChanged()) {
      updateBody.cohort_language = buildCohortLanguage(state.billingCodes);
    }

    return updateBody;
  };

  // Update the handleUpdate function to use the dynamic request body
  const handleUpdate = async () => {
    if (!state.editingVolumeId) return;

    setIsLoading(true);

    const updateBody = buildUpdateRequestBody();

    try {
      await updateCohort({
        id: state.editingVolumeId,
        data: updateBody,
      }).unwrap();

      setNotification({
        title: 'Success',
        message: 'Volume updated successfully',
        type: 'success',
      });

      // Refresh folder data to ensure items in folders show updated information
      mutateAccountFolders();

      // RTK Query will automatically refetch the updated cohort data via cache invalidation

      // Create the updated volume object with proper typing
      const createUpdatedVolumeData = (cohortData) => {
        // Create a properly typed volume data object
        const updatedVolumeData: {
          id: number;
          label: string;
          cohort_language: any;
          scope: 'personal' | 'company';
          owner: any;
          filters: any[];
          created_at?: string;
          status?: string;
          volumeType: 'cohort';
        } = {
          ...cohortData,
          id: parseInt(state.editingVolumeId),
          label: state.newCohortName,
          scope:
            state.selectedType === SavedType.PERSONAL ? 'personal' : 'company',
          filters: filtersFromCohortLanguage(cohortData.cohort_language as any),
          volumeType: 'cohort',
        };

        return updatedVolumeData;
      };

      // Update selected volume if the updated provider cohort is currently selected
      if (
        appliedSavedVolume &&
        appliedSavedVolume.id === parseInt(state.editingVolumeId) &&
        appliedSavedVolume.volumeType === 'cohort' &&
        data
      ) {
        // Create the updated volume data
        const updatedVolumeData = createUpdatedVolumeData(data);

        // Update the volume in the current location
        setSavedVolume(updatedVolumeData);
      }

      handleClose();
    } catch (error: any) {
      console.error('Error updating volume:', error);

      setNotification({
        title: 'Error',
        message:
          error?.data?.error_message ||
          error?.message ||
          'Failed to update volume. Please try again.',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Disable buttons if form is empty or loading
  const isFormInvalid =
    !state.newCohortName.trim() ||
    state.billingCodes.groups.length === 0 ||
    state.billingCodes.groups.some((group) => group.codes.length === 0) ||
    isLoading ||
    isFetching;

  // Add a function to check if changes have been made
  const hasChanges = () => {
    if (!originalState) return false;

    // Compare name
    if (state.newCohortName !== originalState.newCohortName) return true;

    // Compare type (personal/company)
    if (state.selectedType !== originalState.selectedType) return true;

    // Compare top-level operator
    if (
      state.billingCodes.anyAllOperator !==
      originalState.billingCodes.anyAllOperator
    )
      return true;

    // Compare groups - we'll do a more thorough check
    if (
      state.billingCodes.groups.length !==
      originalState.billingCodes.groups.length
    )
      return true;

    // Check each group's properties and codes
    for (let i = 0; i < state.billingCodes.groups.length; i++) {
      const currentGroup = state.billingCodes.groups[i];
      const originalGroup = originalState.billingCodes.groups[i];

      // Check operator and inverse
      if (currentGroup.operator !== originalGroup.operator) return true;
      if (currentGroup.inverse !== originalGroup.inverse) return true;

      // Check codes length
      if (currentGroup.codes.length !== originalGroup.codes.length) return true;

      // Check each code
      for (let j = 0; j < currentGroup.codes.length; j++) {
        if (currentGroup.codes[j].id !== originalGroup.codes[j].id) return true;
        if (currentGroup.codes[j].modifier !== originalGroup.codes[j].modifier)
          return true;
      }
    }

    return false;
  };

  // Update the button disable logic
  const isUpdateDisabled =
    isFormInvalid || // Original validation
    !hasChanges(); // New check for changes

  return (
    <>
      <Drawer
        isOpen={isDrawerOpen('editEncountersDrawer')}
        onClose={handleClose}
        hideBackdrop
        size="base"
        data-testid="edit-encounters-drawer"
        anchor="right"
        variant="temporary"
      >
        <Box className="flex flex-col h-full relative">
          {/* Header */}
          <Drawer.Header className="flex items-center w-full bg-sky-100 px-4">
            <Typography className="text-lg text-primary font-bold">
              Editing Code Set
            </Typography>
            <Drawer.CloseButton
              className="text-primary flex items-center ml-auto"
              data-testid="edit-encounters-drawer-close-button"
            />
          </Drawer.Header>

          {/* Content Area */}
          <Drawer.Content>
            {isFetching ? (
              <Box className="flex justify-center items-center p-8">
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box className="flex justify-center items-center p-8">
                <Typography className="text-red-500">
                  Error loading volume data. Please try again.
                </Typography>
              </Box>
            ) : (
              <>
                <BillingCodes />
                <SearchPreview />
                <SaveVolumeType />
              </>
            )}
          </Drawer.Content>

          {/* Footer with Save and Update buttons */}
          <Drawer.Footer>
            <Box className="flex w-full gap-4">
              <Button
                variant="solid"
                className="flex-1 !rounded-full bg-neutral-500 hover:bg-neutral-600 text-white font-bold"
                onClick={handleSaveAsNew}
                disabled={isFormInvalid || isLoading}
              >
                Save As New...
              </Button>
              <Button
                variant="solid"
                className="flex-1 !rounded-full bg-sky-600 hover:bg-sky-800 text-white font-bold"
                onClick={handleUpdate}
                disabled={isUpdateDisabled}
                loading={isLoading}
              >
                Update
              </Button>
            </Box>
          </Drawer.Footer>
        </Box>
      </Drawer>

      {/* Render SaveNewVolumeDialog outside of the Drawer */}
      <SaveNewVolumeDialog
        isOpen={isSaveAsNewDialogOpen}
        onClose={() => setIsSaveAsNewDialogOpen(false)}
        volumeType={VolumeType.ENCOUNTERS}
        currentName={state.newCohortName}
        currentType={state.selectedType}
        billingCodes={state.billingCodes}
        onSaveSuccess={() => {
          // RTK Query will auto-invalidate the cache
        }}
        originalState={originalState}
      />
    </>
  );
};

export default EditEncountersDrawer;
